import React from 'react';
import styled, {keyframes} from 'styled-components';
import {Row, Col, Typography} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  TrophyOutlined,
  RocketOutlined,
  HeartOutlined,
  StarOutlined,
} from '@ant-design/icons';

const {Title, Paragraph} = Typography;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Styled Components
const FeaturesContainer = styled.section`
  width: 100%;
  max-width: 100vw;
  padding: ${props => props.theme.spacing?.[32] || '8rem'}
    ${props => props.theme.spacing?.[6] || '1.5rem'} ${props => props.theme.spacing?.[20] || '5rem'};
  background: ${props => props.theme.colors?.neutral?.[950] || '#0a0a0a'};
  position: relative;
  overflow: hidden;
  margin: 0;

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[20] || '5rem'}
      ${props => props.theme.spacing?.[4] || '1rem'} ${props => props.theme.spacing?.[16] || '4rem'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 50% 50%, rgba(249, 115, 22, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing?.[16] || '4rem'};
  animation: ${fadeInUp} 1s ease-out;
`;

const SectionTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.['4xl'] || '2.25rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.black || '900'};
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.['3xl'] || '1.875rem'};
    }
  }
`;

const SectionSubtitle = styled(Paragraph)`
  && {
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
    max-width: 600px;
    margin: 0 auto;
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
  }
`;

const FeatureCard = styled.div`
  background: ${props => props.theme.colors?.glass?.light || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(20px);
  border-radius: ${props => props.theme.borderRadius?.['2xl'] || '1rem'};
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${props => props.theme.spacing?.[8] || '2rem'};
  height: 100%;
  transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 1s ease-out;
  animation-fill-mode: both;
  animation-delay: ${props => props.delay || '0s'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -200%;
    width: 200%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props =>
      props.theme.boxShadow?.xl ||
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
    border-color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};

    &::before {
      left: 100%;
    }

    .feature-icon {
      animation: ${pulse} 1s ease-in-out;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[6] || '1.5rem'};
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${props => props.theme.borderRadius?.['2xl'] || '1rem'};
  background: ${props =>
    props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing?.[6] || '1.5rem'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 70%
    );
    background-size: 200% 200%;
    animation: ${shimmer} 2s infinite;
  }

  .anticon {
    font-size: 2rem;
    color: white;
    z-index: 1;
    position: relative;
  }
`;

const FeatureTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.xl || '1.25rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.bold || '700'};
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    margin-bottom: ${props => props.theme.spacing?.[3] || '0.75rem'};
  }
`;

const FeatureDescription = styled(Paragraph)`
  && {
    color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
    margin-bottom: 0;
  }
`;

const features = [
  {
    icon: <UserOutlined />,
    title: 'Build Your Portfolio',
    description:
      'Create an interactive portfolio that showcases your creative skills and attracts brand partnerships.',
    delay: '0.1s',
  },
  {
    icon: <TeamOutlined />,
    title: 'Connect with Creatives',
    description:
      'Network with fellow college creators and collaborate on exciting projects and campaigns.',
    delay: '0.2s',
  },
  {
    icon: <TrophyOutlined />,
    title: 'Track Your Growth',
    description:
      'Monitor your skill development and increase your visibility to potential brand partners.',
    delay: '0.3s',
  },
  {
    icon: <RocketOutlined />,
    title: 'Launch Your Career',
    description:
      'Get discovered by brands looking for fresh Gen-Z talent for internships and campaigns.',
    delay: '0.4s',
  },
  {
    icon: <HeartOutlined />,
    title: 'Authentic Partnerships',
    description:
      'Work with brands that align with your values and creative vision for meaningful collaborations.',
    delay: '0.5s',
  },
  {
    icon: <StarOutlined />,
    title: 'Exclusive Opportunities',
    description:
      'Access exclusive creative opportunities and paid projects from our brand partners.',
    delay: '0.6s',
  },
];

const ModernFeatures = () => {
  return (
    <FeaturesContainer>
      <ContentWrapper>
        <SectionHeader>
          <SectionTitle level={2}>Everything You Need to Succeed</SectionTitle>
          <SectionSubtitle>
            Our platform provides all the tools and connections you need to build your creative
            career and connect with leading brands.
          </SectionSubtitle>
        </SectionHeader>

        <Row gutter={[32, 32]}>
          {features.map((feature, index) => (
            <Col xs={24} md={12} lg={8} key={index}>
              <FeatureCard delay={feature.delay}>
                <FeatureIcon className="feature-icon">{feature.icon}</FeatureIcon>
                <FeatureTitle level={3}>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            </Col>
          ))}
        </Row>
      </ContentWrapper>
    </FeaturesContainer>
  );
};

export default ModernFeatures;
